import { Component } from '@theme/component';

/**
 * Lists the component products of a Shopify Bundles product on the product page.
 *
 * Liquid has no access to bundle components on the product page (only the cart's
 * `line_item.item_components`), so the list comes from the Storefront API
 * (`ProductVariant.requiresComponents` / `components`). The request is a token-less,
 * same-origin call answered by the Online Store channel. A public Storefront access
 * token (theme setting, passed as `data-token`) is only needed while the store is
 * password-protected, when token-less calls fail with "Online Store channel is locked."
 *
 * @typedef {Object} BundleComponent
 * @property {string} title - Product title, with the variant title appended when it isn't the default
 * @property {number} quantity - How many of this component the bundle contains
 *
 * @typedef {Object} BeBundleContentsRefs
 * @property {HTMLUListElement} list - The list the components render into
 * @property {HTMLElement} [note] - Theme-editor-only hint, hidden once components render
 *
 * @extends {Component<BeBundleContentsRefs>}
 */
class BeBundleContentsComponent extends Component {
  requiredRefs = ['list'];

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  /**
   * A variant change re-renders the product section through the Section Rendering API,
   * which restores the empty server markup — render again (from cache) when that happens.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#load();
  }

  async #load() {
    const { variantId, apiUrl, token } = this.dataset;
    if (!variantId || !apiUrl) return;

    /** @type {BundleComponent[]} */
    let components = [];

    try {
      components = await fetchComponents(apiUrl, variantId, token);
    } catch (error) {
      console.warn('[be-bundle-contents]', error instanceof Error ? error.message : error);
    }

    // Ignore a stale response once the variant changed mid-flight or the block left the page.
    if (!this.isConnected || this.dataset.variantId !== variantId) return;

    this.#render(components);
  }

  /**
   * @param {BundleComponent[]} components
   */
  #render(components) {
    const { list, note } = this.refs;
    const isBundle = components.length > 0;

    list.replaceChildren(...components.map((component) => this.#renderItem(component)));

    // The theme editor keeps the block visible (with its hint) so merchants can find it.
    this.hidden = !isBundle && !Shopify.designMode;
    if (note) note.hidden = isBundle;
  }

  /**
   * @param {BundleComponent} component
   * @returns {HTMLLIElement}
   */
  #renderItem({ title, quantity }) {
    const item = document.createElement('li');
    item.className = 'be-bundle-contents__item';
    item.append(title);

    if (quantity > 1) {
      const quantityLabel = document.createElement('span');
      quantityLabel.className = 'be-bundle-contents__quantity';
      quantityLabel.textContent = ` × ${quantity}`;
      item.append(quantityLabel);
    }

    return item;
  }
}

const QUERY = `
  query BeBundleComponents($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        requiresComponents
        components(first: 50) {
          nodes {
            quantity
            productVariant {
              title
              product {
                title
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * In-flight and settled requests, keyed by variant. Failures are evicted so a later
 * variant change (or editor re-render) can retry.
 * @type {Map<string, Promise<BundleComponent[]>>}
 */
const cache = new Map();

/**
 * @param {string} apiUrl
 * @param {string} variantId
 * @param {string} [token]
 * @returns {Promise<BundleComponent[]>}
 */
function fetchComponents(apiUrl, variantId, token) {
  const key = `${apiUrl}:${variantId}`;
  let pending = cache.get(key);

  if (!pending) {
    pending = requestComponents(apiUrl, variantId, token).catch((error) => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, pending);
  }

  return pending;
}

/**
 * @param {string} apiUrl
 * @param {string} variantId
 * @param {string} [token]
 * @returns {Promise<BundleComponent[]>}
 */
async function requestComponents(apiUrl, variantId, token) {
  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['X-Shopify-Storefront-Access-Token'] = token;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: QUERY,
      variables: { id: `gid://shopify/ProductVariant/${variantId}` },
    }),
  });

  const json = await response.json().catch(() => null);

  if (json?.errors?.length) {
    throw new Error(json.errors.map((/** @type {{ message: string }} */ error) => error.message).join('; '));
  }

  if (!response.ok || !json) {
    throw new Error(`Storefront API returned ${response.status}`);
  }

  /** @type {Array<{ quantity: number, productVariant: { title: string, product: { title: string } } | null }>} */
  const nodes = json.data?.node?.components?.nodes ?? [];

  return nodes.flatMap(({ quantity, productVariant }) => {
    // A component whose product isn't published to the channel answering the request comes back as null.
    if (!productVariant) return [];

    const { product, title: variantTitle } = productVariant;
    const title =
      variantTitle && variantTitle !== 'Default Title' ? `${product.title} – ${variantTitle}` : product.title;

    return [{ title, quantity }];
  });
}

if (!customElements.get('be-bundle-contents-component')) {
  customElements.define('be-bundle-contents-component', BeBundleContentsComponent);
}
